import React, { useState, useEffect, useMemo } from 'react';
// Rich Text Editor for Discipline Boxes using ReactQuill
import { Save, Eye, Target, Shield, BookOpen } from 'lucide-react';
import axios from 'axios';
import { useToast } from './Toast';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Map box numbers to shooting branch codes
const boxNumberToBranch = {
  1: 'NSF',
  2: 'DFS', 
  3: 'DSSN',
  4: 'SWS',
  5: 'NSU',
  6: 'NROF',
  7: 'NMSF',
  8: 'NJFF',
  9: 'NBIF',
  10: 'NBSF',
  11: 'NSSF'
};

const DisciplineBoxEditor = ({ organizationId }) => {
  const toast = useToast();
  const [boxes, setBoxes] = useState([]);
  const [activeBox, setActiveBox] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // ReactQuill modules and formats
  const quillModules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  }), []);

  const quillFormats = [
    'bold', 'italic', 'underline',
    'header',
    'list', 'bullet',
    'link'
  ];

  const [editContent, setEditContent] = useState({
    1: { title: '', content: '' },
    2: { title: '', content: '' },
    3: { title: '', content: '' }
  });

  useEffect(() => {
    if (organizationId) {
      loadDisciplineBoxes();
    }
  }, [organizationId]);

  const loadDisciplineBoxes = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/organizations/${organizationId}/discipline-boxes`, { headers });
      const boxesData = response.data.boxes || [];
      
      console.log('Loaded discipline boxes:', boxesData);
      setBoxes(boxesData);
      
      // Initialize edit content from loaded boxes
      const newEditContent = {};
      if (boxesData.length > 0) {
        boxesData.forEach(box => {
          newEditContent[box.box_number] = {
            title: box.title || '',
            content: box.content || ''
          };
        });
        console.log('Edit content initialized from boxes:', newEditContent);
      } else {
        console.log('No boxes found, backend should create defaults on next request');
        // If no boxes exist, the backend will create defaults when we call the API
        // Initialize empty edit content for now
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach(boxNum => {
          newEditContent[boxNum] = {
            title: '',
            content: ''
          };
        });
      }
      
      setEditContent(newEditContent);
    } catch (error) {
      console.error('Failed to load discipline boxes:', error);
      toast.error('Kunne ikke laste disciplinbokser fra server');
      
      // Initialize empty content on error
      const emptyContent = {};
      [1, 2, 3].forEach(boxNum => {
        emptyContent[boxNum] = { title: '', content: '' };
      });
      setEditContent(emptyContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (boxNumber) => {
    setSaving(true);
    console.log(`Saving box ${boxNumber}:`, editContent[boxNumber]);
    
    try {
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      
      const updateData = {
        title: editContent[boxNumber]?.title || '',
        content: editContent[boxNumber]?.content || ''
      };

      console.log(`Sending PUT request to: ${API_BASE}/api/organizations/${organizationId}/discipline-boxes/${boxNumber}`);
      console.log('Update data:', updateData);

      const response = await axios.put(
        `${API_BASE}/api/organizations/${organizationId}/discipline-boxes/${boxNumber}`,
        updateData,
        { headers }
      );
      
      console.log('Save response:', response.data);
      toast.success(`${boxNumberToBranch[boxNumber]} disiplin-boks lagret!`);
      
      // Refresh data to ensure UI is updated
      await loadDisciplineBoxes();
    } catch (error) {
      console.error('Failed to save discipline box:', error);
      toast.error(`Kunne ikke lagre ${boxNumberToBranch[boxNumber]} disiplin-boks: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (boxNumber) => {
    const box = editContent[boxNumber];
    const previewWindow = window.open('', '_blank', 'width=600,height=800,scrollbars=yes,resizable=yes');
    
    if (previewWindow) {
      previewWindow.document.write(`
        <!DOCTYPE html>
        <html lang="no">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Forhåndsvisning - ${box.title}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; 
              max-width: 600px; 
              margin: 0 auto; 
              padding: 20px; 
              line-height: 1.6; 
              color: #333; 
              background-color: #1f2937;
              color: #e5e7eb;
            }
            h1, h2, h3, h4, h5, h6 { color: #fff; margin-top: 0; }
            .container { 
              background-color: #374151; 
              border-radius: 8px; 
              padding: 24px; 
              border: 1px solid #4b5563;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 1px solid #4b5563;
            }
            .preview-badge {
              position: fixed;
              top: 10px;
              right: 10px;
              background-color: #f59e0b;
              color: #000;
              padding: 5px 10px;
              border-radius: 4px;
              font-weight: bold;
              font-size: 12px;
              z-index: 1000;
            }
            .close-btn {
              position: fixed;
              top: 10px;
              left: 10px;
              background-color: #ef4444;
              color: white;
              border: none;
              width: 30px;
              height: 30px;
              border-radius: 50%;
              cursor: pointer;
              font-size: 18px;
              font-weight: bold;
              z-index: 1000;
            }
            ul { padding-left: 20px; }
            li { margin-bottom: 5px; }
          </style>
        </head>
        <body>
          <div class="preview-badge">📋 FORHÅNDSVISNING</div>
          <button class="close-btn" onclick="window.close()" title="Lukk forhåndsvisning">&times;</button>
          <div class="container">
            <div class="header">
              <h1>${box.title}</h1>
              <p><strong>{boxNumberToBranch[boxNumber]}</strong> • Aktivlogg Disiplin-boks</p>
            </div>
            
            <div class="content">
              ${box.content}
            </div>
            
            <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #4b5563; text-align: center; color: #9ca3af; font-size: 14px;">
              <p>Forhåndsvisning generert: ${new Date().toLocaleDateString('no-NO')} kl. ${new Date().toLocaleTimeString('no-NO')}</p>
            </div>
          </div>
        </body>
        </html>
      `);
      
      previewWindow.document.close();
    }
  };

  const getBoxIcon = (boxNumber) => {
    // Different icons for different box numbers
    const icons = {
      1: <Target className="h-5 w-5 text-yellow-400" />,       // NSF
      2: <Shield className="h-5 w-5 text-green-400" />,        // DFS
      3: <BookOpen className="h-5 w-5 text-blue-400" />,       // DSSN
      4: <Target className="h-5 w-5 text-yellow-600" />,       // SWS
      5: <Shield className="h-5 w-5 text-gray-400" />,         // NSU
      6: <BookOpen className="h-5 w-5 text-indigo-400" />,     // NROF
      7: <Target className="h-5 w-5 text-pink-400" />,         // NMSF
      8: <Shield className="h-5 w-5 text-emerald-400" />,      // NJFF
      9: <BookOpen className="h-5 w-5 text-cyan-400" />,       // NBIF
      10: <Target className="h-5 w-5 text-red-400" />,         // NBSF
      11: <Shield className="h-5 w-5 text-teal-400" />         // NSSF
    };
    return icons[boxNumber] || <BookOpen className="h-5 w-5 text-yellow-400" />;
  };

  const getBoxTitle = (boxNumber) => {
    return boxNumberToBranch[boxNumber] || `Boks ${boxNumber}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Box Selector Tabs - wraps on mobile */}
      <div className="flex flex-wrap gap-1 bg-gray-700 rounded-lg p-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((boxNum) => (
          <button
            key={boxNum}
            onClick={() => setActiveBox(boxNum)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeBox === boxNum
                ? 'bg-yellow-500 text-gray-900'
                : 'text-gray-300 hover:text-white hover:bg-gray-600'
            }`}
          >
            {getBoxIcon(boxNum)}
            <span className="ml-1">{getBoxTitle(boxNum)}</span>
          </button>
        ))}
      </div>

      {/* Editor for Active Box */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getBoxIcon(activeBox)}
            <h4 className="text-lg font-medium text-white ml-2">
              {getBoxTitle(activeBox)}
            </h4>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => handlePreview(activeBox)}
              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 transition-colors"
            >
              <Eye className="h-4 w-4 mr-1" />
              Forhåndsvis
            </button>
            <button
              onClick={() => handleSave(activeBox)}
              disabled={saving}
              className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-500 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Lagrer...' : 'Lagre'}
            </button>
          </div>
        </div>

        {/* Title Editor */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Boktittel
          </label>
          <input
            type="text"
            value={editContent[activeBox]?.title || ''}
            onChange={(e) => setEditContent(prev => ({
              ...prev,
              [activeBox]: { ...prev[activeBox], title: e.target.value }
            }))}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
            placeholder={`Tittel for boks ${activeBox}`}
          />
        </div>

        {/* Rich Text Editor using ReactQuill WYSIWYG */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Bokinnhold
          </label>
          <div className="discipline-quill-editor bg-gray-700 rounded-lg overflow-hidden">
            <ReactQuill
              theme="snow"
              value={editContent[activeBox]?.content || ''}
              onChange={(content) => setEditContent(prev => ({
                ...prev,
                [activeBox]: { ...prev[activeBox], content: content }
              }))}
              modules={quillModules}
              formats={quillFormats}
              placeholder={`Skriv inn innhold for ${getBoxTitle(activeBox)}...`}
              className="text-white"
            />
          </div>
          <p className="text-gray-500 text-xs mt-2">
            💡 <strong>Tips:</strong> Bruk verktøylinjen for formatering. Støtter overskrifter, lister, lenker og mer.
          </p>
          
          {/* Custom styles for dark mode Quill editor */}
          <style>{`
            .discipline-quill-editor .ql-toolbar {
              background-color: #374151;
              border: none;
              border-bottom: 1px solid #4B5563;
            }
            .discipline-quill-editor .ql-toolbar button {
              color: #D1D5DB;
            }
            .discipline-quill-editor .ql-toolbar button:hover {
              color: #FBBF24;
            }
            .discipline-quill-editor .ql-toolbar button.ql-active {
              color: #FBBF24;
            }
            .discipline-quill-editor .ql-toolbar .ql-stroke {
              stroke: #D1D5DB;
            }
            .discipline-quill-editor .ql-toolbar .ql-fill {
              fill: #D1D5DB;
            }
            .discipline-quill-editor .ql-toolbar button:hover .ql-stroke {
              stroke: #FBBF24;
            }
            .discipline-quill-editor .ql-toolbar button.ql-active .ql-stroke {
              stroke: #FBBF24;
            }
            .discipline-quill-editor .ql-toolbar .ql-picker {
              color: #D1D5DB;
            }
            .discipline-quill-editor .ql-toolbar .ql-picker-options {
              background-color: #374151;
              border: 1px solid #4B5563;
            }
            .discipline-quill-editor .ql-toolbar .ql-picker-item {
              color: #D1D5DB;
            }
            .discipline-quill-editor .ql-toolbar .ql-picker-item:hover {
              color: #FBBF24;
            }
            .discipline-quill-editor .ql-container {
              border: none;
              font-size: 14px;
              min-height: 200px;
            }
            .discipline-quill-editor .ql-editor {
              background-color: #374151;
              color: #F9FAFB;
              min-height: 200px;
            }
            .discipline-quill-editor .ql-editor.ql-blank::before {
              color: #9CA3AF;
              font-style: normal;
            }
            .discipline-quill-editor .ql-editor h1,
            .discipline-quill-editor .ql-editor h2,
            .discipline-quill-editor .ql-editor h3 {
              color: #FBBF24;
            }
            .discipline-quill-editor .ql-editor a {
              color: #60A5FA;
            }
            .discipline-quill-editor .ql-editor ul,
            .discipline-quill-editor .ql-editor ol {
              padding-left: 1.5em;
            }
          `}</style>
        </div>

        {/* Save Progress Indicator */}
        {saving && (
          <div className="text-center py-2">
            <div className="inline-flex items-center text-yellow-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400 mr-2"></div>
              Lagrer boks {activeBox}...
            </div>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-gray-700/30 border border-gray-600/30 rounded-lg p-4">
        <h5 className="text-gray-300 font-medium text-sm mb-2">📋 Hvordan bruke editoren</h5>
        <div className="text-gray-400 text-xs space-y-1">
          <p>• <strong>NSF:</strong> Informasjon om NSF treningsvåpen og krav</p>
          <p>• <strong>DFS:</strong> Informasjon om Det Frivillige Skyttervesen</p>
          <p>• <strong>DSSN:</strong> Informasjon om dynamisk sportsskyting</p>
          <p>• Og tilsvarende for alle andre aktive skyttergrener</p>
          <p>• Bruk "Forhåndsvis" for å se hvordan boksen vil se ut for medlemmene</p>
          <p>• Husk å lagre endringene for hver boks individuelt</p>
        </div>
      </div>
    </div>
  );
};

export default DisciplineBoxEditor;